import UsersDirectory from '../components/users/UsersDirectory';
import {
  filterDirectory,
  DirectoryUser,
} from '../components/users/userDirectoryModel';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Plus, RefreshCw, Key } from 'lucide-react';
import { EditUserModal } from '../components/UserManagementModals';
import { ConfirmActionModal } from '../components/ConfirmActionModalV2';
import { PageContainer, PageHeader } from '../components/layout/Page';

type User = DirectoryUser;

interface Organization {
  id: string;
  name: string;
}

export default function UserManagement() {
  const { user: currentUser, userRole } = useAuth();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const request = useRef(0);
  const invalidateRequests = useCallback(() => {
    request.current++;
  }, []);
  const [loadError, setLoadError] = useState(false);
  const [orgError, setOrgError] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const searchTerm = params.get('search') || '';
  const roleFilter =
    userRole === 'reviewer'
      ? 'all'
      : ['normal', 'reviewer', 'admin'].includes(params.get('role') || '')
        ? params.get('role')!
        : 'all';
  const statusFilter = ['active', 'inactive', 'never_logged'].includes(
    params.get('status') || '',
  )
    ? params.get('status')!
    : 'all';
  const orgFilter = params.get('org') || 'all';

  // Form states
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'normal' as const,
    organization_id: '',
    is_moral: false,
    selectedOrganizations: [] as string[],
    primaryOrganization: '' as string,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Edit modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editOption, setEditOption] = useState<
    'email' | 'add_email' | 'organizations' | 'toggle_active' | null
  >(null);
  const [editFormData, setEditFormData] = useState({
    newEmail: '',
    additionalEmail: '',
    selectedOrganizations: [] as string[],
    primaryOrganizations: {} as Record<string, boolean>,
    stagedEmails: [] as string[],
    is_moral: false,
    is_internal: false,
  });

  // Confirmation modal states
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    action: 'resend' | 'delete' | 'delete-email' | 'toggle_active' | null;
    userId: string;
    userEmail: string;
    emailId?: string;
  }>({
    isOpen: false,
    action: null,
    userId: '',
    userEmail: '',
    emailId: '',
  });

  useEffect(() => {
    if (currentUser) {
      fetchOrganizations();
    }
  }, [currentUser]);

  useEffect(() => {
    if (loading || !location.hash.startsWith('#user-')) return;
    const target = document.getElementById(location.hash.slice(1));
    target?.focus();
    target?.scrollIntoView?.({ block: 'center' });
  }, [loading, location.hash]);

  const fetchUsers = useCallback(async () => {
    const id = ++request.current;
    try {
      setLoading(true);
      setLoadError(false);
      setUsers([]);

      let query = supabase
        .from('users')
        .select(
          `
          *,
          user_organization!left(
            organization:organizations(id, name, hexcode),
            is_active,
            is_main
          ),
          user_extra_mails!left(
            id,
            email,
            created_at
          )
        `,
        )
        .eq('is_deleted', false)
        .order('full_name', { ascending: true, nullsFirst: false })
        .order('id', { ascending: true });

      if (userRole === 'reviewer') query = query.eq('role', 'normal');

      // Fetch only directory data; document calculations belong in document views.
      const allUsers: User[] = [];
      for (let offset = 0; ; offset += 500) {
        const { data, error } = await query.range(offset, offset + 499);
        if (error) throw error;
        if (id !== request.current) return;
        allUsers.push(...(data || []));
        if (!data || data.length < 500) break;
      }
      if (id === request.current) setUsers(allUsers);
    } catch (error) {
      if (id === request.current) setLoadError(true);
    } finally {
      if (id === request.current) setLoading(false);
    }
  }, [userRole]);

  useEffect(() => {
    if (currentUser) {
      fetchUsers();
    }
    return invalidateRequests;
  }, [currentUser, fetchUsers, invalidateRequests]);

  const fetchOrganizations = async () => {
    setOrgError(false);
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name')
        .order('name', { ascending: true });

      if (error) throw error;
      setOrganizations(data || []);
    } catch (error) {
      setOrgError(true);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchUsers();
    setRefreshing(false);
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, password });
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.email.trim()) {
      errors.email = 'El correo es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'El correo no es válido';
    }

    if (!formData.full_name.trim()) {
      errors.full_name = 'El nombre es requerido';
    }

    if (!formData.password) {
      errors.password = 'La contraseña es requerida';
    }

    if (formData.selectedOrganizations.length === 0) {
      errors.organization_id = 'Debe seleccionar al menos una organización';
    }
    if (
      formData.selectedOrganizations.length > 0 &&
      !formData.primaryOrganization
    ) {
      errors.organization_id = 'Debe seleccionar una organización principal';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setCreatingUser(true);
    try {
      // For new users, we'll use the primary organization for emails
      const primaryOrg = organizations.find(
        (org) => org.id === formData.primaryOrganization,
      );

      if (!primaryOrg) {
        alert('Error: No se encontró la organización principal');
        setCreatingUser(false);
        return;
      }

      const { data: definitions, error: definitionsError } = await supabase
        .from('document_definitions')
        .select('id')
        .eq('is_active', true);
      if (definitionsError) throw definitionsError;

      // Use Edge Function to create user with admin privileges
      const { data, error } = await supabase.functions.invoke(
        'admin-user-management',
        {
          body: {
            email: formData.email,
            password: formData.password,
            fullName: formData.full_name,
            role: formData.role,
            organizationId: formData.primaryOrganization, // Primary org for backward compatibility
            selectedOrganizations: formData.selectedOrganizations,
            primaryOrganization: formData.primaryOrganization,
            documentDefinitionIds: (definitions || []).map((def) => def.id),
            emailOrganizationId: formData.primaryOrganization,
            emailOrganizationName: primaryOrg.name,
            is_moral: formData.is_moral,
          },
        },
      );

      if (error) {
        throw new Error(error.message || 'Failed to create user');
      }

      if (data?.success) {
        alert(
          `Usuario creado exitosamente.\n\nEmail: ${formData.email}\nContraseña: ${formData.password}\n\nSe ha enviado un email de invitación al usuario.\n\nNota: Si el email de confirmación está habilitado en Supabase, el usuario deberá confirmar su email antes de poder iniciar sesión.`,
        );
        setShowCreateForm(false);
        resetForm();
        await fetchUsers();
      } else {
        throw new Error(data?.error || 'Unknown error occurred');
      }
    } catch (error: any) {
      console.error('Error creating user:', error);

      // Provide more specific error messages
      let errorMessage = 'Error al crear el usuario';
      if (
        error.message?.includes('already registered') ||
        error.message?.includes('User already registered')
      ) {
        errorMessage = 'Este correo electrónico ya está registrado';
      } else if (error.message?.includes('duplicate key')) {
        errorMessage =
          'El usuario ya existe. Intente con otro correo electrónico';
      } else if (error.message) {
        errorMessage = error.message;
      }

      alert(errorMessage);
    } finally {
      setCreatingUser(false);
    }
  };

  const handleConfirmAction = () => {
    if (confirmModal.action === 'resend') {
      executeResendInvitation(confirmModal.userId, confirmModal.userEmail);
    } else if (confirmModal.action === 'delete') {
      executeDeleteUser(confirmModal.userId, confirmModal.userEmail);
    } else if (confirmModal.action === 'delete-email' && confirmModal.emailId) {
      executeDeleteExtraEmail(confirmModal.emailId);
    } else if (confirmModal.action === 'toggle_active') {
      executeToggleActive(confirmModal.userId);
    }
  };

  const handleResendInvitation = (userId: string, userEmail: string) => {
    setConfirmModal({
      isOpen: true,
      action: 'resend',
      userId,
      userEmail,
    });
  };

  const executeResendInvitation = async (userId: string, userEmail: string) => {
    try {
      // Get user data
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select(
          `
          *,
          user_organization!left(
            organization:organizations(*),
            is_active,
            is_main
          ),
          user_extra_mails(id, email)
        `,
        )
        .eq('id', userId)
        .single();

      if (userError || !userData) {
        throw new Error('No se pudo obtener información del usuario');
      }

      // Get user's primary organization
      const userPrimaryOrg = userData.user_organization?.find(
        (uo) => uo.is_active && uo.is_main,
      );
      const userActiveOrg = userData.user_organization?.find(
        (uo) => uo.is_active,
      );

      if (!userPrimaryOrg && !userActiveOrg) {
        throw new Error('El usuario no tiene organizaciones activas asignadas');
      }

      // Use primary org if exists, otherwise use any active org
      const emailOrg =
        userPrimaryOrg?.organization || userActiveOrg?.organization;

      // Call admin-user-management with isResend flag
      // Get additional emails
      const additionalEmails =
        userData.user_extra_mails
          ?.filter((mail) => mail.email)
          ?.map((mail) => mail.email) || [];

      const { data, error } = await supabase.functions.invoke(
        'admin-user-management',
        {
          body: {
            userId: userId,
            email: userEmail,
            fullName: userData.full_name,
            role: userData.role,
            organizationId: emailOrg!.id,
            // Use the user's primary org (or active org) for email
            emailOrganizationId: emailOrg!.id,
            emailOrganizationName: emailOrg!.name,
            isResend: true,
            additionalEmails: additionalEmails,
          },
        },
      );

      if (error) {
        throw new Error(error.message || 'Error al reenviar invitación');
      }

      if (data?.success) {
        alert(`✅ Nueva invitación enviada a ${userEmail}`);
        await fetchUsers();
      } else {
        throw new Error(data?.error || 'Error desconocido');
      }
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      alert(`Error al reenviar la invitación: ${error.message}`);
    }
  };

  const handleDeleteUser = (userId: string, userEmail: string) => {
    setConfirmModal({
      isOpen: true,
      action: 'delete',
      userId,
      userEmail,
    });
  };

  const executeDeleteUser = async (userId: string, userEmail: string) => {
    try {
      // Call the new delete-user Edge Function
      const { data, error } = await supabase.functions.invoke('swift-action', {
        // Name swift-action becasue name wasnt updated
        body: {
          userId: userId,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to delete user');
      }

      if (data?.success) {
        await fetchUsers();
        alert(
          `Usuario eliminado exitosamente: ${data.deletedUser?.email || userEmail}`,
        );
      } else {
        throw new Error(data?.error || 'Unknown error occurred');
      }
    } catch (error: any) {
      console.error('Error deleting user:', error);
      alert(`Error al eliminar el usuario: ${error.message}`);
    }
  };

  const executeToggleActive = async (userId: string) => {
    try {
      if (!editingUser) return;

      const newActiveState = !editingUser.is_active;

      const { error } = await supabase
        .from('users')
        .update({
          is_active: newActiveState,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);

      if (error) {
        alert(`Error al cambiar el estado: ${error.message}`);
        setConfirmModal({
          isOpen: false,
          action: null,
          userId: '',
          userEmail: '',
          emailId: '',
        });
        return;
      }

      alert(
        `Usuario ${newActiveState ? 'activado' : 'desactivado'} exitosamente`,
      );

      // Close both modals
      setConfirmModal({
        isOpen: false,
        action: null,
        userId: '',
        userEmail: '',
        emailId: '',
      });
      setShowEditModal(false);
      setEditOption(null);
      setEditingUser(null);

      // Refresh users list
      await fetchUsers();
    } catch (error: any) {
      console.error('Error toggling user active status:', error);
      alert(`Error al cambiar el estado: ${error.message}`);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      full_name: '',
      password: '',
      role: 'normal',
      organization_id: '',
      is_moral: false,
      selectedOrganizations: [],
      primaryOrganization: '',
    });
    setFormErrors({});
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowEditModal(true);
    setEditOption(null);
    // Initialize edit form data with current user data
    const activeOrgs =
      user.user_organization?.filter((uo) => uo.is_active) || [];
    const primaryOrgs: Record<string, boolean> = {};

    activeOrgs.forEach((uo) => {
      if (uo.is_main) {
        primaryOrgs[uo.organization.id] = true;
      }
    });

    setEditFormData({
      newEmail: '',
      additionalEmail: '',
      selectedOrganizations: activeOrgs.map((uo) => uo.organization.id),
      primaryOrganizations: primaryOrgs,
      stagedEmails: [],
      is_moral: user.is_moral || false,
      is_internal: user.is_internal || false,
    });
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingUser(null);
    setEditOption(null);
    setEditFormData({
      newEmail: '',
      additionalEmail: '',
      selectedOrganizations: [],
      primaryOrganizations: {},
      stagedEmails: [],
      is_moral: false,
      is_internal: false,
    });
  };

  const handleDeleteExtraEmail = (emailId: string) => {
    if (!editingUser) return;

    const emailToDelete = editingUser.user_extra_mails?.find(
      (em) => em.id === emailId,
    );
    if (!emailToDelete || !emailToDelete.email) {
      alert('Correo no encontrado');
      return;
    }

    setConfirmModal({
      isOpen: true,
      action: 'delete-email',
      userId: editingUser.id,
      userEmail: emailToDelete.email,
      emailId,
    });
  };

  const executeDeleteExtraEmail = async (emailId: string) => {
    if (!editingUser) return;

    try {
      const emailToDelete = editingUser.user_extra_mails?.find(
        (em) => em.id === emailId,
      );

      if (!emailToDelete) {
        alert('Correo no encontrado');
        return;
      }

      const { error } = await supabase
        .from('user_extra_mails')
        .delete()
        .eq('id', emailId);

      if (error) {
        throw error;
      }

      alert('Correo adicional eliminado exitosamente');

      // Update local state to reflect the change immediately
      const updatedUser = {
        ...editingUser,
        user_extra_mails: editingUser.user_extra_mails?.filter(
          (em) => em.id !== emailId,
        ),
      };
      setEditingUser(updatedUser);

      // Also update the users list
      await fetchUsers();
    } catch (error: any) {
      console.error('Error deleting extra email:', error);
      alert(`Error al eliminar correo adicional: ${error.message}`);
    }
  };

  const handleRemoveStagedEmail = (email: string) => {
    setEditFormData((prev) => ({
      ...prev,
      stagedEmails: prev.stagedEmails.filter((e) => e !== email),
    }));
  };

  const handleAddEmailToStaging = () => {
    const email = editFormData.additionalEmail.trim();

    if (!email) {
      alert('Por favor ingrese un correo electrónico válido');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert('Por favor ingrese un correo electrónico válido');
      return;
    }

    // Check if email is the primary email
    if (email === editingUser?.email) {
      alert('Este correo es el correo principal del usuario');
      return;
    }

    // Check if email already exists in extra mails
    const existingEmail = editingUser?.user_extra_mails?.find(
      (em) => em.email === email,
    );
    if (existingEmail) {
      alert('Este correo adicional ya está registrado');
      return;
    }

    // Check if email is already in staging
    if (editFormData.stagedEmails.includes(email)) {
      alert('Este correo ya está en la lista para agregar');
      return;
    }

    // Add to staging
    setEditFormData((prev) => ({
      ...prev,
      stagedEmails: [...prev.stagedEmails, email],
      additionalEmail: '', // Clear input
    }));
  };

  const handleSaveAdditionalEmails = async () => {
    if (!editingUser) return;

    try {
      if (editFormData.stagedEmails.length === 0) {
        alert('No hay correos para agregar');
        return;
      }

      // Validate all emails don't exist for other users
      for (const email of editFormData.stagedEmails) {
        // Check in users table
        const { data: existingUser } = await supabase
          .from('users')
          .select('id')
          .eq('email', email)
          .single();

        if (existingUser) {
          alert(`El correo ${email} ya está registrado por otro usuario`);
          return;
        }

        // Check in extra mails for other users
        const { data: existingExtraMail } = await supabase
          .from('user_extra_mails')
          .select('id')
          .eq('email', email)
          .neq('user_id', editingUser.id)
          .single();

        if (existingExtraMail) {
          alert(
            `El correo ${email} ya está registrado como correo adicional de otro usuario`,
          );
          return;
        }
      }

      // Insert all staged emails
      const emailsToInsert = editFormData.stagedEmails.map((email) => ({
        user_id: editingUser.id,
        email: email,
      }));

      const { error: insertError } = await supabase
        .from('user_extra_mails')
        .insert(emailsToInsert);

      if (insertError) {
        throw insertError;
      }

      alert(
        `${editFormData.stagedEmails.length} correo(s) adicional(es) agregado(s) exitosamente`,
      );
      await fetchUsers();
      setEditOption(null); // Return to main modal instead of closing
    } catch (error: any) {
      console.error('Error saving additional emails:', error);
      alert(`Error al guardar correos adicionales: ${error.message}`);
    }
  };

  const handleSaveOrganizations = async () => {
    if (!editingUser) return;

    try {
      // Get current user organizations
      const { data: currentOrgs, error: fetchError } = await supabase
        .from('user_organization')
        .select('organization_id, is_active, is_main')
        .eq('user_id', editingUser.id);

      if (fetchError) {
        throw fetchError;
      }

      const currentOrgIds =
        currentOrgs
          ?.filter((org) => org.is_active)
          .map((org) => org.organization_id) || [];

      // Organizations to add (not in current)
      const orgsToAdd = editFormData.selectedOrganizations.filter(
        (orgId) => !currentOrgIds.includes(orgId),
      );

      // Organizations to remove (in current but not selected)
      const orgsToRemove = currentOrgIds.filter(
        (orgId) => !editFormData.selectedOrganizations.includes(orgId),
      );

      // Organizations to update (already exist but need to update is_main)
      const orgsToUpdate = editFormData.selectedOrganizations.filter((orgId) =>
        currentOrgIds.includes(orgId),
      );

      // Process additions
      for (const orgId of orgsToAdd) {
        const existingOrg = currentOrgs?.find(
          (org) => org.organization_id === orgId,
        );

        if (existingOrg) {
          // Reactivate existing inactive organization
          await supabase
            .from('user_organization')
            .update({
              is_active: true,
              is_main: editFormData.primaryOrganizations[orgId] || false,
              updated_at: new Date().toISOString(),
            })
            .eq('user_id', editingUser.id)
            .eq('organization_id', orgId);
        } else {
          // Create new organization relationship
          await supabase.from('user_organization').insert({
            user_id: editingUser.id,
            organization_id: orgId,
            is_active: true,
            is_main: editFormData.primaryOrganizations[orgId] || false,
          });
        }
      }

      // Process removals (soft delete)
      for (const orgId of orgsToRemove) {
        await supabase
          .from('user_organization')
          .update({
            is_active: false,
            is_main: false,
            updated_at: new Date().toISOString(),
          })
          .eq('user_id', editingUser.id)
          .eq('organization_id', orgId);
      }

      // Process updates (update is_main status)
      for (const orgId of orgsToUpdate) {
        const isMain = editFormData.primaryOrganizations[orgId] || false;
        const currentOrg = currentOrgs?.find(
          (org) => org.organization_id === orgId,
        );

        if (currentOrg && currentOrg.is_main !== isMain) {
          await supabase
            .from('user_organization')
            .update({
              is_main: isMain,
              updated_at: new Date().toISOString(),
            })
            .eq('user_id', editingUser.id)
            .eq('organization_id', orgId);
        }
      }

      // Refresh users list
      await fetchUsers();
      alert('Organizaciones actualizadas exitosamente');
      setEditOption(null); // Return to main modal instead of closing
    } catch (error: any) {
      console.error('Error updating organizations:', error);
      alert(`Error al actualizar organizaciones: ${error.message}`);
    }
  };

  const handleSaveEditUser = async () => {
    if (!editingUser || !editOption) return;

    try {
      if (editOption === 'email') {
        // Check if there are any changes to save
        const emailChanged =
          editFormData.newEmail &&
          editFormData.newEmail.trim() !== '' &&
          editFormData.newEmail !== editingUser.email;
        const isMoralChanged = editFormData.is_moral !== editingUser.is_moral;
        const isInternalChanged =
          editFormData.is_internal !== editingUser.is_internal;

        if (!emailChanged && !isMoralChanged && !isInternalChanged) {
          alert('No hay cambios para guardar');
          return;
        }

        // Validate new email if it was changed
        if (emailChanged && editFormData.newEmail === editingUser.email) {
          alert('El nuevo correo debe ser diferente al actual');
          return;
        }

        // Build update object
        const updateData: any = {
          updated_at: new Date().toISOString(),
        };

        if (emailChanged) {
          updateData.email = editFormData.newEmail;
        }

        if (isMoralChanged) {
          updateData.is_moral = editFormData.is_moral;
        }

        if (isInternalChanged) {
          updateData.is_internal = editFormData.is_internal;
        }

        // Update users table
        const { error } = await supabase
          .from('users')
          .update(updateData)
          .eq('id', editingUser.id);

        if (error) {
          if (error.code === '23505') {
            // Unique constraint violation
            alert('Este correo electrónico ya está registrado');
          } else {
            alert(`Error al actualizar los datos: ${error.message}`);
          }
          return;
        }

        let successMessage = 'Datos actualizados exitosamente: ';
        const changes = [];
        if (emailChanged) changes.push('correo electrónico');
        if (isMoralChanged) changes.push('tipo de persona');
        if (isInternalChanged)
          changes.push(
            editFormData.is_internal
              ? 'marcado como interno'
              : 'desmarcado como interno',
          );

        successMessage += changes.join(', ');

        alert(successMessage);
        await fetchUsers();
        setEditOption(null); // Return to main modal instead of closing
      } else if (editOption === 'add_email') {
        // Handle additional emails
        await handleSaveAdditionalEmails();
      } else if (editOption === 'organizations') {
        // Handle organizations update
        await handleSaveOrganizations();
      } else if (editOption === 'toggle_active') {
        // Handle toggle active/inactive - show confirmation modal
        setConfirmModal({
          isOpen: true,
          action: 'toggle_active',
          userId: editingUser.id,
          userEmail: editingUser.email,
        });
      }
    } catch (error: any) {
      console.error('Error saving user edit:', error);
      alert('Error al guardar los cambios');
    }
  };

  const getRoleText = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'reviewer':
        return 'Revisor';
      default:
        return 'Usuario';
    }
  };

  const filteredUsers = filterDirectory(
    users,
    {
      search: searchTerm,
      role: roleFilter,
      status: statusFilter,
      org: orgFilter,
    },
    params.get('sort') || 'name',
  );
  const [draft, setDraft] = useState({
    search: searchTerm,
    role: roleFilter,
    status: statusFilter,
    org: orgFilter,
  });
  useEffect(() => {
    setDraft({
      search: searchTerm,
      role: roleFilter,
      status: statusFilter,
      org: orgFilter,
    });
  }, [searchTerm, roleFilter, statusFilter, orgFilter]);
  const [showFilters, setShowFilters] = useState(
    roleFilter !== 'all' || statusFilter !== 'all' || orgFilter !== 'all',
  );
  const applyFilters = (event: React.FormEvent) => {
    event.preventDefault();
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        Object.entries(draft).forEach(([key, value]) => {
          if (!value || value === 'all') next.delete(key);
          else next.set(key, value);
        });
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  };
  const clearFilters = () => {
    setDraft({ search: '', role: 'all', status: 'all', org: 'all' });
    setParams({});
  };
  const activeFilters = [
    roleFilter !== 'all' ? getRoleText(roleFilter) : '',
    statusFilter !== 'all'
      ? {
          active: 'Activos',
          inactive: 'Inactivos',
          never_logged: 'Sin acceso previo',
        }[statusFilter]
      : '',
    orgFilter !== 'all'
      ? organizations.find((org) => org.id === orgFilter)?.name ||
        'Organización seleccionada'
      : '',
  ].filter(Boolean);

  return (
    <PageContainer>
        <PageHeader
          title="Usuarios"
          description="Encuentra a una persona y abre su expediente documental."
          actions={<>
            <button
              onClick={handleRefresh}
              disabled={loading || refreshing}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm disabled:opacity-50"
            >
              Actualizar
            </button>
            {userRole === 'admin' && (
              <button
                onClick={() => setShowCreateForm(true)}
                className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800"
              >
                Nuevo usuario
              </button>
            )}
          </>}
        />
        {/* Create User Form */}
        {showCreateForm && (
          <div className="bg-white p-6 rounded-xl border border-slate-200 mb-6">
            <h2 className="text-xl font-semibold mb-4">Crear Nuevo Usuario</h2>
            <form onSubmit={handleCreateUser}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Correo Electrónico *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.email && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre Completo *
                  </label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) =>
                      setFormData({ ...formData, full_name: e.target.value })
                    }
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.full_name
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                  />
                  {formErrors.full_name && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.full_name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contraseña *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.password
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={generatePassword}
                      className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                    >
                      <Key className="w-4 h-4" />
                    </button>
                  </div>
                  {formErrors.password && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.password}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rol
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value as any })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="normal">Usuario</option>
                    <option value="reviewer">Revisor</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organizaciones *
                  </label>
                  <div
                    className={`border rounded-lg p-2 max-h-40 overflow-y-auto ${
                      formErrors.organization_id
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                  >
                    {organizations.map((org) => (
                      <div
                        key={org.id}
                        className="mb-2 p-2 hover:bg-gray-50 rounded"
                      >
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={formData.selectedOrganizations.includes(
                              org.id,
                            )}
                            onChange={(e) => {
                              if (e.target.checked) {
                                // Add organization
                                const newSelected = [
                                  ...formData.selectedOrganizations,
                                  org.id,
                                ];
                                setFormData({
                                  ...formData,
                                  selectedOrganizations: newSelected,
                                  // If it's the first org, make it primary
                                  primaryOrganization:
                                    newSelected.length === 1
                                      ? org.id
                                      : formData.primaryOrganization,
                                });
                              } else {
                                // Remove organization
                                const newSelected =
                                  formData.selectedOrganizations.filter(
                                    (id) => id !== org.id,
                                  );
                                setFormData({
                                  ...formData,
                                  selectedOrganizations: newSelected,
                                  // If removing primary, clear it
                                  primaryOrganization:
                                    formData.primaryOrganization === org.id
                                      ? ''
                                      : formData.primaryOrganization,
                                });
                              }
                            }}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm">{org.name}</span>
                        </label>
                        {formData.selectedOrganizations.includes(org.id) && (
                          <div className="ml-6 mt-1">
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                name="primaryOrg"
                                checked={
                                  formData.primaryOrganization === org.id
                                }
                                onChange={() =>
                                  setFormData({
                                    ...formData,
                                    primaryOrganization: org.id,
                                  })
                                }
                                className="w-3 h-3 text-green-600 border-gray-300 focus:ring-green-500"
                              />
                              <span className="text-xs text-gray-600">
                                Principal
                              </span>
                            </label>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {formErrors.organization_id && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.organization_id}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de persona (SAT)
                  </label>
                  {formData.role === 'normal' ? (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setFormData({ ...formData, is_moral: false })
                        }
                        className={`flex-1 py-2 px-3 rounded-lg border-2 transition-all text-sm ${
                          !formData.is_moral
                            ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                            : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        Física
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData({ ...formData, is_moral: true })
                        }
                        className={`flex-1 py-2 px-3 rounded-lg border-2 transition-all text-sm ${
                          formData.is_moral
                            ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                            : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        Moral
                      </button>
                    </div>
                  ) : (
                    <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm">
                      No aplica para este rol
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={creatingUser}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {creatingUser ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Creando...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Crear Usuario
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        <section
          aria-label="Directorio de usuarios"
          className="rounded-xl border border-slate-200 bg-white"
        >
          <form
            onSubmit={applyFilters}
            className="border-b border-slate-200 p-4 sm:p-5"
          >
            <div className="flex flex-wrap items-end gap-3">
              <label className="min-w-0 flex-1 basis-64 text-sm font-medium">
                Buscar por nombre o correo
                <input
                  type="search"
                  value={draft.search}
                  onChange={(event) =>
                    setDraft({ ...draft, search: event.target.value })
                  }
                  placeholder="Nombre o correo electrónico"
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 font-normal focus:ring-2 focus:ring-blue-600"
                />
              </label>
              <button
                type="submit"
                className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800"
              >
                Buscar
              </button>
              <button
                type="button"
                aria-expanded={showFilters}
                aria-controls="directory-filters"
                onClick={() => setShowFilters((value) => !value)}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                Filtros
                {activeFilters.length ? ` (${activeFilters.length})` : ''}
              </button>
            </div>
            <div id="directory-filters" hidden={!showFilters} className="mt-4">
              <div className="grid gap-3 sm:grid-cols-3">
                {userRole === 'admin' && (
                  <label className="text-sm font-medium">
                    Rol
                    <select
                      value={draft.role}
                      onChange={(event) =>
                        setDraft({ ...draft, role: event.target.value })
                      }
                      className="mt-1 block w-full rounded-lg border border-slate-300 p-2 font-normal"
                    >
                      <option value="all">Todos los roles</option>
                      <option value="normal">Usuario</option>
                      <option value="reviewer">Revisor</option>
                      <option value="admin">Administrador</option>
                    </select>
                  </label>
                )}
                <label className="text-sm font-medium">
                  Estado
                  <select
                    value={draft.status}
                    onChange={(event) =>
                      setDraft({ ...draft, status: event.target.value })
                    }
                    className="mt-1 block w-full rounded-lg border border-slate-300 p-2 font-normal"
                  >
                    <option value="all">Todos los estados</option>
                    <option value="active">Activos</option>
                    <option value="inactive">Inactivos</option>
                    <option value="never_logged">Sin acceso previo</option>
                  </select>
                </label>
                <label className="text-sm font-medium">
                  Organización
                  <select
                    value={draft.org}
                    onChange={(event) =>
                      setDraft({ ...draft, org: event.target.value })
                    }
                    className="mt-1 block w-full rounded-lg border border-slate-300 p-2 font-normal"
                  >
                    <option value="all">Todas las organizaciones</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <button
                type="submit"
                className="mt-3 text-sm font-medium text-blue-700 underline"
              >
                Aplicar filtros
              </button>
            </div>
            {(searchTerm || activeFilters.length > 0) && (
              <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <span>
                  Aplicados:{' '}
                  {[searchTerm ? `“${searchTerm}”` : '', ...activeFilters]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-blue-700 underline"
                >
                  Limpiar filtros
                </button>
              </div>
            )}
            {orgError && (
              <p role="alert" className="mt-3 text-sm text-red-800">
                No se pudieron cargar las organizaciones.{' '}
                <button
                  type="button"
                  onClick={fetchOrganizations}
                  className="underline"
                >
                  Reintentar organizaciones
                </button>
              </p>
            )}
          </form>
          <UsersDirectory
            users={filteredUsers}
            loading={loading}
            error={loadError}
            retry={fetchUsers}
            isAdmin={userRole === 'admin'}
            currentUserId={currentUser?.id}
            onEdit={handleEditUser}
            onResend={(user) => handleResendInvitation(user.id, user.email)}
            onDelete={(user) => handleDeleteUser(user.id, user.email)}
          />
        </section>

        {/* Edit User Modal */}
        <EditUserModal
          showModal={showEditModal}
          editingUser={editingUser}
          editOption={editOption}
          editFormData={editFormData}
          organizations={organizations}
          onClose={handleCloseEditModal}
          onEditOptionChange={setEditOption}
          onFormDataChange={setEditFormData}
          onSave={handleSaveEditUser}
          onDeleteExtraEmail={handleDeleteExtraEmail}
          onAddEmailToStaging={handleAddEmailToStaging}
          onRemoveStagedEmail={handleRemoveStagedEmail}
        />

        {/* Confirmation Modal */}
        <ConfirmActionModal
          isOpen={confirmModal.isOpen}
          onClose={() =>
            setConfirmModal({
              isOpen: false,
              action: null,
              userId: '',
              userEmail: '',
              emailId: '',
            })
          }
          onConfirm={handleConfirmAction}
          title={
            confirmModal.action === 'resend'
              ? 'Reenviar Invitación'
              : confirmModal.action === 'delete'
                ? 'Eliminar Usuario'
                : confirmModal.action === 'toggle_active'
                  ? editingUser?.is_active
                    ? 'Desactivar Usuario'
                    : 'Activar Usuario'
                  : 'Eliminar Correo Adicional'
          }
          message={
            confirmModal.action === 'resend'
              ? `¿Enviar nueva invitación a ${confirmModal.userEmail}?`
              : confirmModal.action === 'delete'
                ? `¿Está seguro de eliminar al usuario ${confirmModal.userEmail}? Esta acción marcará al usuario como eliminado y no podrá iniciar sesión.`
                : confirmModal.action === 'toggle_active'
                  ? editingUser?.is_active
                    ? `¿Está seguro de desactivar al usuario ${confirmModal.userEmail}? El usuario permanecerá en el sistema pero será marcado como inactivo.`
                    : `¿Está seguro de activar al usuario ${confirmModal.userEmail}? El usuario volverá a estar activo en el sistema.`
                  : `¿Está seguro de eliminar el correo adicional ${confirmModal.userEmail}?`
          }
          confirmText={
            confirmModal.action === 'resend'
              ? 'Enviar Invitación'
              : confirmModal.action === 'toggle_active'
                ? editingUser?.is_active
                  ? 'Desactivar'
                  : 'Activar'
                : 'Eliminar'
          }
          cancelText="Cancelar"
          isDestructive={
            confirmModal.action === 'delete' ||
            confirmModal.action === 'delete-email'
          }
        />
    </PageContainer>
  );
}
